import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ChevronRight, Users, Calendar, Anchor } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEUR = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

const formatDateRange = (start, end) => {
  if (!start) return '';
  try {
    const s = new Date(start + 'T00:00:00');
    const e = end ? new Date(end + 'T00:00:00') : null;
    const monthsFR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
    const fmt = (d) => `${d.getDate()} ${monthsFR[d.getMonth()]}`;
    if (e) {
      if (s.getMonth() === e.getMonth()) {
        return `du ${s.getDate()} au ${fmt(e)} ${e.getFullYear()}`;
      }
      return `du ${fmt(s)} au ${fmt(e)} ${e.getFullYear()}`;
    }
    return fmt(s);
  } catch {
    return start;
  }
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  try {
    const target = new Date(dateStr + 'T00:00:00').getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today.getTime()) / 86400000);
    return diff;
  } catch { return null; }
};

const LastMinuteCard = ({ item, index }) => {
  const hasDiscount = Number(item.discounted_price) > 0 && Number(item.original_price) > Number(item.discounted_price);
  const discountPct = hasDiscount ? Math.round((1 - item.discounted_price / item.original_price) * 100) : 0;
  const days = daysUntil(item.date_start);
  const isUrgent = days !== null && days >= 0 && days <= 30;

  return (
    <Link
      to={item.link_url || '/croisieres'}
      className="group relative block rounded-2xl overflow-hidden bg-gradient-to-br from-[#0A2540] via-[#0F2E4D] to-[#0A2540] text-white shadow-[0_10px_40px_-12px_rgba(10,37,64,0.4)] hover:shadow-[0_20px_60px_-12px_rgba(196,114,79,0.35)] transition-all duration-500 hover:-translate-y-1"
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`last-minute-card-${item.id}`}
    >
      {/* Gold corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#D4AF37]/20 via-transparent to-transparent pointer-events-none" />
      {/* Subtle grain */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)', backgroundSize: '24px 24px' }} />

      {/* Image */}
      {item.image_url && (
        <div className="relative h-44 overflow-hidden">
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/40 to-transparent" />
        </div>
      )}

      <div className="relative p-6 sm:p-7">
        {/* Badges */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C4956A] text-[#0A2540] text-[10px] font-bold uppercase tracking-[0.18em]">
            <Sparkles className="h-3 w-3" /> {item.badge_text || 'Place rare'}
          </span>
          {hasDiscount && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-rose-500/90 text-white text-[10px] font-bold uppercase tracking-wider">
              −{discountPct}%
            </span>
          )}
          {isUrgent && days >= 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-[10px] font-semibold">
              {days === 0 ? "Départ aujourd'hui" : days === 1 ? 'J−1' : `J−${days}`}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight mb-1.5">
          {item.title}
        </h3>
        {item.subtitle && (
          <p className="text-white/65 text-sm leading-relaxed mb-4">{item.subtitle}</p>
        )}

        {/* Meta */}
        <div className="space-y-1.5 mb-5 text-sm text-white/80">
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
            <span className="capitalize">{formatDateRange(item.date_start, item.date_end)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Anchor className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
            <span>
              <strong className="text-white">{item.cabins_remaining}</strong> cabine{item.cabins_remaining > 1 ? 's' : ''} restante{item.cabins_remaining > 1 ? 's' : ''}
            </span>
          </div>
          {item.places_remaining > 0 && (
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
              <span>
                <strong className="text-white">{item.places_remaining}</strong> place{item.places_remaining > 1 ? 's' : ''} disponible{item.places_remaining > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Price + CTA */}
        <div className="flex items-end justify-between pt-4 border-t border-white/10">
          {item.original_price > 0 ? (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-0.5">{item.price_label || 'à partir de'}</div>
              <div className="flex items-baseline gap-2">
                {hasDiscount && (
                  <span className="text-white/40 line-through text-base">{fmtEUR(item.original_price)}</span>
                )}
                <span className="font-display text-3xl font-bold text-[#D4AF37]">
                  {fmtEUR(hasDiscount ? item.discounted_price : item.original_price)}
                </span>
              </div>
            </div>
          ) : <div />}
          <span className="inline-flex items-center gap-1 text-[#D4AF37] text-sm font-semibold uppercase tracking-[0.12em] group-hover:gap-2 transition-all">
            Réserver <ChevronRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const LastMinuteSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/last-minute`)
      .then((r) => { if (alive) setItems(r.data || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!items || items.length === 0) return null;

  return (
    <section className="relative py-20 sm:py-28 bg-[#F8F4ED] overflow-hidden" data-testid="last-minute-section">
      {/* Subtle gold diagonal */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(135deg, transparent 0%, transparent 60%, rgba(212,175,55,0.06) 100%)',
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
              <Sparkles className="h-3.5 w-3.5 text-[#A88838]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A88838]">Place rare · Dernières disponibilités</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2540] leading-tight">
              Saisissez les <em className="not-italic text-[#C4724F]">dernières places</em>
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl">
              Profitez de nos toutes dernières disponibilités pour embarquer cet été. Quelques cabines encore ouvertes sur des départs confirmés &mdash; à saisir avant que le large ne nous appelle.
            </p>
          </div>
          <Link
            to="/croisieres"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-[0.14em] text-[#0A2540] hover:text-[#C4724F] transition-colors"
          >
            Toutes nos croisières <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Grid */}
        <div className={`grid gap-5 ${items.length === 1 ? 'sm:grid-cols-1 max-w-2xl' : items.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
          {items.map((item, i) => (
            <LastMinuteCard key={item.id} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LastMinuteSection;
